/**
 * The postMessage wire protocol between the HOST PAGE (the loader) and the
 * WIDGET IFRAME (the Vue app). This module is the only place either side
 * describes the other, and both import it, so a message that one side sends and
 * the other does not understand is a type error rather than a silent no-op.
 *
 * THREE PROPERTIES THIS FILE EXISTS TO HOLD.
 *
 * 1. EVERY send names an explicit targetOrigin. There is no `'*'` in this package
 *    and there must never be one: a `'*'` from the iframe hands the transcript to
 *    whatever page happens to be framing us, and a `'*'` from the host hands the
 *    customer's page state to whatever we happen to be framing.
 *
 * 2. EVERY receive validates `event.origin` AND `event.source` before it looks at
 *    `event.data`. `window.addEventListener('message')` is a public inbox: any
 *    frame, opener or extension content script on the page can post into it. A
 *    handler that switches on `data.type` without checking who sent it is a
 *    cross-origin RPC endpoint, and on the host side that endpoint is inside the
 *    customer's origin.
 *
 * 3. The envelope is VERSIONED (`p: 'omni.widget.v1'`). A customer's page caches
 *    the loader; the iframe app is redeployed whenever we like. The two will be
 *    of different ages in production, permanently, so the version is on the wire
 *    rather than assumed.
 *
 * Field names are short (`p`, `t`) because the loader ships to every page view of
 * every customer site and these literals are in it twice over.
 */

export const PROTOCOL_ID = 'omni.widget.v1';

/**
 * The host page's colours, as the browser computed them (P2-124, ADR-0248).
 *
 * The iframe is on another origin and cannot read a single style of the page it
 * sits on; the loader can, so it samples and the iframe decides. Every value is
 * the RAW string `getComputedStyle` or the `<meta>` tag gave — the loader parses
 * nothing, because every byte of parsing would ship to every page view of every
 * customer, and because the iframe has to re-validate it anyway: to the iframe
 * this is a message from a page it does not control. The keys are one or two
 * letters for the same reason `p` and `t` are (see the header).
 *
 * Optional on `init` because the two ends are of different ages in production:
 * a cached loader from before this field sends no palette, and the iframe then
 * paints its own defaults exactly as it did before.
 */
export interface HostPalette {
  /** `<meta name="theme-color">` content, verbatim, or null. */
  readonly m: string | null;
  /** Computed `background-color` of `<html>`, then `<body>` — the later opaque one is on top. */
  readonly bg: readonly string[];
  /** Computed `color` of `<body>`. */
  readonly fg: string;
  /** Computed `background-color` of the first buttons on the page, in document order. */
  readonly btn: readonly string[];
  /** Computed `color` of the first links on the page, in document order. */
  readonly a: readonly string[];
}

/** Sent by the host page's loader, received by the iframe app. */
export type HostMessage =
  /**
   * The handshake. Carries no origin field ON PURPOSE: the iframe reads the host
   * origin from `event.origin`, which the browser guarantees and the sender
   * cannot forge, and a field would just be a forgeable second opinion.
   */
  | {
      readonly p: typeof PROTOCOL_ID;
      readonly t: 'init';
      readonly theme: string;
      readonly locale: string | null;
      readonly palette?: HostPalette;
      /**
       * The signed-in user's JWT when the site called `identify` before the
       * handshake (P2-126/P2-127, ADR-0249), so the FIRST bootstrap is already the
       * verified person and no anonymous person is minted for a signed-in user.
       * Optional for the same reason `palette` is: loaders of different ages.
       */
      readonly jwt?: string;
      /**
       * Pairs `jwt` with its `done` reply, exactly as on `identify` (P2-126):
       * without it a pre-handshake `omniWidget.identify()` would have nothing to
       * resolve its promise. Present only when `jwt` is; the same value on every
       * repeat of `init`, and the iframe answers it once.
       */
      readonly n?: number;
    }
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'open' }
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'close' }
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'theme'; readonly theme: string }
  /**
   * The site's own user signed in after the handshake (ADR-0249). `jwt` is
   * HS256-signed by the SITE'S backend with the brand's identity secret; the
   * iframe never verifies it, it hands it to the API, which does. `n` pairs the
   * call with its `done` reply so `omniWidget.identify()` can resolve.
   */
  | {
      readonly p: typeof PROTOCOL_ID;
      readonly t: 'identify';
      readonly jwt: string;
      readonly n: number;
    }
  /**
   * The site's own user signed out (ADR-0249): end this visitor session, forget
   * the device, and come back as a fresh anonymous visitor — Intercom's
   * `shutdown()` followed by `boot()`, as one message.
   */
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'logout'; readonly n: number };

/** Sent by the iframe app, received by the host page's loader. */
export type WidgetMessage =
  /**
   * The app has booted and knows the brand's configuration. `accent` is the
   * launcher's colour: the button lives on the customer's page, so it cannot read
   * our design tokens, and the brand accent is only known after the session call.
   * The loader re-validates it as a hex colour before assigning — a value that
   * reached us from the API is still untrusted input on someone else's page.
   */
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'ready'; readonly accent: string | null }
  /**
   * The launcher's colour, known before `ready`: the accent the iframe picked
   * from the host page's own palette, sent right after the handshake so the
   * button takes the site's colour without waiting for the session call. The
   * loader treats it exactly like `ready.accent` — hex or nothing.
   */
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'accent'; readonly accent: string }
  /** Count of agent messages the visitor has not seen. Drives the launcher badge. */
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'unread'; readonly count: number }
  /** The visitor closed the panel from inside it (its own ✕, or Escape). */
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'close' }
  /**
   * The launcher's accessible name, translated. The launcher lives on the
   * customer's page, so the loader cannot carry copy for two locales — but an
   * icon-only button with no accessible name is a dead control for a screen
   * reader, so the iframe (which has the catalogue) sends the label up.
   */
  | { readonly p: typeof PROTOCOL_ID; readonly t: 'label'; readonly text: string }
  /**
   * The outcome of `identify` / `logout` number `n` (ADR-0249). `r` is a stable
   * reason code when `ok` is false (`identity_invalid`, `identity_not_configured`,
   * `unavailable`) — never a server message, which would be our copy on their page.
   */
  | {
      readonly p: typeof PROTOCOL_ID;
      readonly t: 'done';
      readonly n: number;
      readonly ok: boolean;
      readonly r?: string;
    };

/** The narrow shape both sides check before trusting anything else on the message. */
interface Envelope {
  readonly p: string;
  readonly t: string;
}

/**
 * Structural check on untrusted `event.data`.
 *
 * Deliberately shallow: it proves the envelope, not the payload. The payload is
 * narrowed by the discriminated union at the point of use, where the compiler can
 * see which variant `t` selected.
 */
export function isEnvelope(data: unknown): data is Envelope {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const candidate = data as { p?: unknown; t?: unknown };
  return candidate.p === PROTOCOL_ID && typeof candidate.t === 'string';
}

/** The parts of a `MessageEvent` that decide whether it may be believed. */
export interface MessageProvenance {
  readonly origin: string;
  readonly source: unknown;
  readonly data: unknown;
}

/**
 * Is this message from the peer we expect?
 *
 * Both checks are load-bearing and neither is sufficient alone. `origin` alone
 * would trust any frame served from the widget origin — including a second,
 * attacker-opened one. `source` alone would trust whatever window reference we
 * hold even if it had navigated somewhere else since.
 *
 * `expectedOrigin` is compared with `===` against a value we computed ourselves.
 * No `startsWith`, no `indexOf`: `'https://widget.example.com'.startsWith` is how
 * `https://widget.example.com.evil.test` gets in.
 */
export function isFromPeer(
  event: MessageProvenance,
  expectedOrigin: string,
  expectedSource: unknown,
): boolean {
  return (
    event.origin === expectedOrigin &&
    expectedSource !== null &&
    event.source === expectedSource &&
    isEnvelope(event.data)
  );
}
